<ion-header [translucent]="true">
  <ion-toolbar>
    <ion-buttons slot="start">
      <ion-button (click)="goBack()">
        <ion-icon name="arrow-back" slot="icon-only"></ion-icon>
      </ion-button>
    </ion-buttons>
    <ion-title>Category Analysis</ion-title>
    <ion-buttons slot="end">
      <ion-button (click)="onRefresh()" [disabled]="isLoading">
        <ion-icon name="refresh-outline" slot="icon-only"></ion-icon>
      </ion-button>
    </ion-buttons>
  </ion-toolbar>
</ion-header>

<ion-content [fullscreen]="true">
  <ion-grid style="margin: 3px">
    <!-- Error State -->
    <ion-row *ngIf="error">
      <ion-col>
        <ion-item color="danger">
          <ion-icon name="warning-outline" slot="start"></ion-icon>
          {{ error }}
        </ion-item>
      </ion-col>
    </ion-row>

    <!-- Loading State -->
    <ion-row *ngIf="isLoading" class="ion-justify-content-center" style="padding: 3rem;">
      <ion-col size="auto" class="ion-text-center">
        <ion-spinner color="primary"></ion-spinner>
        <p style="margin-top: 1rem;">Loading category analysis...</p>
      </ion-col>
    </ion-row>

    <!-- Category Analysis Content -->
    <div *ngIf="!isLoading && !error && analysisData">
      <!-- Category Header -->
      <ion-row>
        <ion-col>
          <ion-card>
            <ion-card-header>
              <ion-card-title>{{ analysisData.category }}</ion-card-title>
              <ion-card-subtitle>
                Category classification analysis and performance metrics
              </ion-card-subtitle>
            </ion-card-header>
            <ion-card-content>
              <ion-grid>
                <ion-row class="ion-text-center">
                  <ion-col>
                    <div style="font-size: 2rem; font-weight: 700; line-height: 1.2;">{{ analysisData.stats.totalArticles }}</div>
                    <div style="font-size: 0.875rem; margin-top: 0.25rem;">Total Articles</div>
                  </ion-col>
                  <ion-col>
                    <div style="font-size: 2rem; font-weight: 700; line-height: 1.2;">{{ analysisData.stats.totalChunks }}</div>
                    <div style="font-size: 0.875rem; margin-top: 0.25rem;">Total Chunks</div>
                  </ion-col>
                  <ion-col>
                    <div style="font-size: 2rem; font-weight: 700; line-height: 1.2;">
                      {{ (analysisData.stats.avgConfidence * 100) | number:'1.1-1' }}%
                    </div>
                    <div style="font-size: 0.875rem; margin-top: 0.25rem;">Avg Confidence</div>
                  </ion-col>
                </ion-row>
              </ion-grid>

              <div style="margin-top: 1.5rem; display: flex; gap: 1rem; align-items: center;">
                <ion-chip [color]="getConfidenceColor(analysisData.stats.avgConfidence)">
                  {{ getConfidenceText(analysisData.stats.avgConfidence) }}
                </ion-chip>
                <span style="font-size: 0.9rem; opacity: 0.7;">
                  First appeared {{ formatDateAge(analysisData.stats.firstAppeared) }} • 
                  Last updated {{ formatDateAge(analysisData.stats.lastUpdated) }}
                </span>
              </div>
            </ion-card-content>
          </ion-card>
        </ion-col>
      </ion-row>

      <!-- Centroid Information -->
      <ion-row *ngIf="analysisData.centroidInfo">
        <ion-col>
          <ion-card>
            <ion-card-header>
              <ion-card-title>Centroid Information</ion-card-title>
              <ion-card-subtitle>Vector similarity matching performance</ion-card-subtitle>
            </ion-card-header>
            <ion-card-content>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;">
                <div>
                  <h3 style="margin: 0; font-size: 1.1rem;">Centroid Strength</h3>
                  <p style="margin: 0.25rem 0 0 0; font-size: 0.9rem; opacity: 0.8;">
                    Based on {{ analysisData.centroidInfo.articleCount }} contributing articles
                  </p>
                </div>
                <ion-chip [color]="getCentroidStrengthColor(analysisData.centroidInfo.strength)">
                  {{ getCentroidStrengthText(analysisData.centroidInfo.strength) }}
                </ion-chip>
              </div>
              
              <div style="font-size: 0.9rem; opacity: 0.7;">
                <p style="margin: 0;">Centroid ID: {{ analysisData.centroidInfo.id }}</p>
                <p style="margin: 0.25rem 0 0 0;">Last updated: {{ analysisData.centroidInfo.lastUpdated | date:'medium' }}</p>
              </div>
            </ion-card-content>
          </ion-card>
        </ion-col>
      </ion-row>

      <!-- No Centroid State -->
      <ion-row *ngIf="!analysisData.centroidInfo">
        <ion-col>
          <ion-card>
            <ion-card-content style="text-align: center; padding: 2rem;">
              <ion-icon name="analytics-outline" style="font-size: 2rem; opacity: 0.5; margin-bottom: 1rem;"></ion-icon>
              <p style="margin: 0;">No centroid available for this category. Centroid-based classification is not possible.</p>
            </ion-card-content>
          </ion-card>
        </ion-col>
      </ion-row>

      <!-- Two-column layout for confidence distribution and sample chunks -->
      <ion-row>
        <!-- Confidence Distribution -->
        <ion-col size="12" size-md="6">
          <ion-card>
            <ion-card-header>
              <ion-card-title>Confidence Distribution</ion-card-title>
              <ion-card-subtitle>Quality breakdown of classifications</ion-card-subtitle>
            </ion-card-header>
            <ion-card-content>
              <div *ngIf="analysisData.confidenceDistribution.length === 0" style="text-align: center; padding: 2rem;">
                <ion-icon name="bar-chart-outline" style="font-size: 2rem; opacity: 0.5; margin-bottom: 1rem;"></ion-icon>
                <p style="margin: 0;">No confidence distribution data available.</p>
              </div>

              <div *ngIf="analysisData.confidenceDistribution.length > 0">
                <div *ngFor="let item of analysisData.confidenceDistribution; trackBy: trackByDistribution" 
                     style="display: flex; justify-content: space-between; align-items: center; padding: 0.75rem 0; border-bottom: 1px solid var(--ion-color-light-shade);">
                  <div style="display: flex; align-items: center; gap: 0.5rem;">
                    <ion-chip [color]="getDistributionColor(item.range)" size="small">
                      {{ item.range }}
                    </ion-chip>
                  </div>
                  <div style="text-align: right;">
                    <div style="font-weight: 600;">{{ item.count }} chunks</div>
                    <div style="font-size: 0.8rem; opacity: 0.7;">{{ item.percentage }}%</div>
                  </div>
                </div>
              </div>
            </ion-card-content>
          </ion-card>
        </ion-col>

        <!-- Sample Chunks -->
        <ion-col size="12" size-md="6">
          <ion-card>
            <ion-card-header>
              <ion-card-title>High-Confidence Samples</ion-card-title>
              <ion-card-subtitle>Top {{ analysisData.sampleChunks.length }} chunks by confidence</ion-card-subtitle>
            </ion-card-header>
            <ion-card-content>
              <div *ngIf="analysisData.sampleChunks.length === 0" style="text-align: center; padding: 2rem;">
                <ion-icon name="documents-outline" style="font-size: 2rem; opacity: 0.5; margin-bottom: 1rem;"></ion-icon>
                <p style="margin: 0;">No sample chunks found for this category.</p>
              </div>

              <div *ngIf="analysisData.sampleChunks.length > 0" style="max-height: 500px; overflow-y: auto;">
                <div *ngFor="let chunk of analysisData.sampleChunks; trackBy: trackByChunk" 
                     style="margin-bottom: 1.5rem; border: 1px solid var(--ion-color-light); border-radius: 8px; padding: 1rem;">
                  
                  <!-- Chunk Header -->
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.75rem;">
                    <div>
                      <h4 style="margin: 0; font-size: 0.9rem; font-weight: 600;">{{ chunk.articleTitle }}</h4>
                      <div style="display: flex; gap: 0.5rem; margin-top: 0.25rem;">
                        <button 
                          (click)="viewArticleAnalysis(chunk.articleId)"
                          style="background: none; border: none; color: var(--ion-color-primary); text-decoration: underline; cursor: pointer; font-size: 0.8rem; padding: 0;">
                          analyze
                        </button>
                        <span style="color: var(--ion-color-medium); font-size: 0.8rem;">•</span>
                        <a 
                          [href]="getArticleUrl(chunk.articleId)"
                          target="_blank"
                          style="color: var(--ion-color-secondary); text-decoration: underline; font-size: 0.8rem;">
                          edit
                        </a>
                      </div>
                    </div>
                    <ion-chip [color]="getConfidenceColor(chunk.confidence)" size="small">
                      {{ (chunk.confidence * 100) | number:'1.1-1' }}%
                    </ion-chip>
                  </div>

                  <!-- Chunk Content -->
                  <div style="background: var(--ion-color-light); padding: 0.75rem; border-radius: 4px;">
                    <p style="margin: 0; line-height: 1.4; font-size: 0.85rem;">
                      {{ truncateContent(chunk.chunkContent) }}
                    </p>
                    <button 
                      *ngIf="chunk.chunkContent.length > 150"
                      (click)="showFullContent(chunk.chunkContent)"
                      style="background: none; border: none; color: var(--ion-color-primary); margin-top: 0.5rem; text-decoration: underline; cursor: pointer; font-size: 0.8rem;">
                      Show full content
                    </button>
                  </div>
                </div>
              </div>
            </ion-card-content>
          </ion-card>
        </ion-col>
      </ion-row>
    </div>
  </ion-grid>
</ion-content>